import { z } from 'zod'

/**
 * The token branch on its own, for the procedures that stay token-only
 * (`saveStep`, `linkToAccount`): one definition, so the union and the
 * token-only inputs cannot drift.
 */
export const resumeTokenAddressSchema = z.object({ token: z.string().min(1) })

/**
 * How an applicant-facing procedure addresses a submission (LG-1056).
 *
 * `{ token }` is the emailed resume link: the bearer credential, for a caller
 * with no session. `{ submissionId }` is the signed-in path: the row must be
 * linked to the caller's own account, so the session is the credential and the
 * id only picks WHICH of the caller's rows (a user can hold more than one).
 *
 * The branches are disjoint by shape. An input carrying BOTH keys is not
 * rejected: zod tries the branches in order, so the token wins, and the
 * matched branch strips the other key. Either way exactly one address
 * reaches the resolver — a request cannot smuggle a second one alongside it.
 *
 * The id branch is `.strict()` so that rule holds for an EMPTY token too:
 * `{ token: '', submissionId }` fails the token branch (`min(1)`) and must
 * not quietly fall through to the id branch as if no token were sent. The
 * strictness is only about `token`: the payload intersections below
 * (`.and(…)`) still admit their own keys, because zod checks each side of an
 * intersection against the same input rather than against the other side's
 * leftovers.
 *
 * `token` and `submissionId` are RESERVED key names across every procedure
 * that intersects this schema with a payload (`submissionAddressSchema.and(…)`,
 * e.g. `createCheckout`'s `priceId`): the whole input is what reaches
 * `loadSubmission`, so a payload field reusing either name would change which
 * credential the request is answered with rather than fail to compile. New
 * payload fields pick another name.
 */
export const submissionAddressSchema = z.union([
  resumeTokenAddressSchema,
  z.object({ submissionId: z.string().uuid() }).strict(),
])

export type SubmissionAddress = z.infer<typeof submissionAddressSchema>

/**
 * A stable string for one address, for React keys and effect deps. Two
 * addresses that name the same row through the same path yield the same
 * key, so a caller re-creating the object literal each render does not
 * remount or re-run anything.
 */
export function submissionAddressKey(address: SubmissionAddress): string {
  return 'token' in address
    ? `token:${address.token}`
    : `submission:${address.submissionId}`
}
